import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Radio,
  Button,
  Tag,
  Space,
  Modal,
  Select,
  message,
  ConfigProvider,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ExclamationCircleFilled } from "@ant-design/icons";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../Context/AuthContext";
import AdminDashboard from "../Layout/AdminDashboard";
import PaymentConfirmationModal from "../Layout/PaymentConfirmationModal";

/* ---------- existing icons ---------- */
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { PiClockClockwiseFill } from "react-icons/pi";
import { RiDraftLine } from "react-icons/ri";
import { LuHardDriveUpload } from "react-icons/lu";
import { FaRegEdit, FaTasks } from "react-icons/fa";
/* ------------------------------------ */

const { confirm } = Modal;

/* ---------- debounce helper ---------- */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
const debounceDelay = 300;
/* ------------------------------------ */

export default function AuditorDashboard() {
  /* ---------- auth ---------- */
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /* ---------- summary cards ---------- */
  const [userSummary, setUserSummary] = useState({});
  const [userSummaryTotal, setUserSummaryTotal] = useState();
  const fetchAuditorSummary = async () => {
    try {
      const res = await axios.get(
        `${apiUrl}/api/auditor/userAuditSummary/${user?._id}`
      );
      setUserSummary(res.data.data ?? {});
      setUserSummaryTotal(res.data.totalAudits);
      console.log(res, "dashboard audit summary")
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- payment table state ---------- */
  const [flattenedTableData, setFlattenedTableData] = useState([]);
  const [sortData, setSortData] = useState("alllist");
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: { current: 1, pageSize: 8, total: 0 },
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [proposalId, setProposalId] = useState(null);
  const [auditorPaymentId, setAuditorPaymentId] = useState(null);

  /* ---------- fetch payment data ---------- */
  const fetchData = useCallback(
    async (status = selectedStatus) => {
      if (!user?._id) return;
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${apiUrl}/api/payment/getAllProposalDetailsAuditor`,
          {
            params: {
              page: tableParams.pagination.current,
              pageSize: tableParams.pagination.pageSize,
              sort: sortData,
              keyword: searchKeyword,
              status,
              auditorId: user._id,
            },
          }
        );
        setFlattenedTableData(
          data.data.map((row, idx) => ({ ...row, key: `${row._id}-${idx}` }))
        );
        setTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            total: data.total,
            current: data.currentPage,
          },
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [
      apiUrl,
      user,
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      sortData,
      searchKeyword,
      selectedStatus,
    ]
  );

  /* ---------- effects ---------- */
  useEffect(() => {
    fetchAuditorSummary();
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------- handlers ---------- */
  const handleTableChange = (pagination) => {
    setTableParams((prev) => ({ ...prev, pagination }));
  };

  const handleFilterChange = (val) => {
    setSelectedStatus(val);
    setTableParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, current: 1 },
    }));
    fetchData(val);
  };

  const debouncedSearch = debounce((kw) => {
    setSearchKeyword(kw);
    setTableParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, current: 1 },
    }));
  }, debounceDelay);

  const handleSearchChange = (e) => debouncedSearch(e.target.value);

  const rowSelection = {
    onChange: (_, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(rows.map((r) => r.key));
    },
  };

  const showDeleteConfirm = () => {
    confirm({
      title: "Are you sure delete?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          await axios.delete(`${apiUrl}/api/payment/deleteFields`, {
            data: selectedRows,
          });
          toast.success("Deleted successfully");
          fetchData();
        } catch (err) {
          toast.error("Delete failed");
        }
      },
    });
  };

  const handleRecordPayment = (proposal_id, auditorPaymentId) => {
    setAuditorPaymentId(auditorPaymentId);
    setProposalId(proposal_id);
    setIsPaymentModalVisible(true);
  };

  const handleCancelPayment = () => setIsPaymentModalVisible(false);

  /* ---------- columns ---------- */
  const columns = [
    {
      title: "Proposal Number",
      dataIndex: "proposal_number",
      key: "proposal_number",
    },
    { title: "FBO Name", dataIndex: "fbo_name", key: "fbo_name" },
    {
      title: "Proposal Value",
      dataIndex: "Proposal_value",
      key: "Proposal_value",
    },
    {
      title: "Total Payment Received",
      dataIndex: "paymentReceived",
      key: "paymentReceived",
    },
    {
      title: "Amount to verify",
      dataIndex: "amounToVerify",
      key: "amountToVerify",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st) => {
        let color =
          { pending: "orange", accepted: "green", rejected: "red" }[st] ??
          "gray";
        return <Tag color={color}>{st?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "View",
      key: "verify_payment",
      render: (_, record) => (
        <Button
          className="bg-blue-500 text-white hover:bg-blue-600"
          onClick={() =>
            handleRecordPayment(record._id, record.auditor_paymentId)
          }
        >
          View
        </Button>
      ),
    },
  ];

  /* ---------- UI ---------- */
  return (
    <AdminDashboard>
      <div className="p-8 bg-blue-50 min-h-screen">
        {/* ---------- Summary Cards ---------- */}
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">Dashboard</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-8">
          {[
            {
              label: "Audits Completed",
              value: userSummary?.approved?.length ?? 0,
              Icon: IoCheckmarkCircleOutline,
              color: "#16a34a",
            },
            {
              label: "Pending Audits",
              value: userSummary?.assigned?.length ?? 0,
              Icon: PiClockClockwiseFill,
              color: "#FD7E14",
            },
            {
              label: "Draft Audits",
              value: userSummary?.draft?.length ?? 0,
              Icon: RiDraftLine,
              color: "#808080",
            },
            {
              label: "Submitted Audits",
              value: userSummary?.submitted?.length ?? 0,
              Icon: LuHardDriveUpload,
              color: "#FFC107",
            },
            {
              label: "Audit Modifications",
              value: userSummary?.modified?.length ?? 0,
              Icon: FaRegEdit,
              color: "#007BFF",
            },
            {
              label: "Assign Audits",
              value: userSummaryTotal?? 0,
              Icon: FaTasks,
              color: "#007BFF",
            },
          ].map(({ label, value, Icon, color }) => (
            <div
              key={label}
              className="flex items-center p-5 bg-white rounded-lg shadow hover:shadow-lg transition"
            >
              <Icon size="1.75rem" color={color} className="mr-4" />
              <div>
                <div className="text-2xl font-semibold text-gray-900">
                  {value}
                </div>
                <div className="text-sm text-gray-600">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ---------- Payment Requests Table ---------- */}
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
          Payment Requests
        </h2>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <Space>
              <Select
                mode="multiple"
                placeholder="Filter by status"
                options={[
                  { value: "pending", label: "Pending" },
                  { value: "accepted", label: "Accepted" },
                  { value: "rejected", label: "Rejected" },
                ]}
                value={selectedStatus}
                onChange={handleFilterChange}
                style={{ width: 240 }}
              />
              <Radio.Group
                value={sortData}
                onChange={(e) => setSortData(e.target.value)}
              >
                <Radio.Button value="alllist">All List</Radio.Button>
                <Radio.Button value="newproposal">New Requests</Radio.Button>
              </Radio.Group>
            </Space>

            <Space>
              <Button
                icon={<DeleteOutlined />}
                disabled={selectedRowKeys.length === 0}
                onClick={showDeleteConfirm}
              >
                Delete
              </Button>
            </Space>
          </div>

          <ConfigProvider
            theme={{
              token: { colorTextHeading: "#4A5568", fontWeightStrong: 600 },
            }}
          >
            <Table
              rowSelection={{ type: "checkbox", ...rowSelection }}
              columns={columns}
              dataSource={flattenedTableData}
              rowKey="key"
              pagination={tableParams.pagination}
              loading={loading}
              onChange={handleTableChange}
            />
          </ConfigProvider>
        </div>

        {/* ---------- Modals ---------- */}
        <PaymentConfirmationModal
          visible={isPaymentModalVisible}
          proposalId={proposalId}
          auditorPaymentId={auditorPaymentId}
          handleCancel={handleCancelPayment}
          viewOnly
        />
      </div>
    </AdminDashboard>
  );
}
